import { axiosAuth } from "./axiosInstances";

// KHAN-239: migrado de api/userbookprogress/{create,update-status,
// update-calificacion,delete,get-or-create,mark-as-completed,
// get-progress/<uid>/<bid>,books-completed/<uid>,mark-points-awarded}/
// (verbos en el path) a api/user-book-progress/ (recurso REST + metodo
// HTTP). Las funciones exportadas de este modulo conservan exactamente su
// firma y la forma de lo que devuelven (mismo shape de `.data` que antes) --
// solo cambia la URL/verbo interno -- para no tener que tocar los
// componentes que las consumen (ReadingView, MyClasses,
// BookCompletedCounter).

// Trailing slash a proposito: Django solo redirige (APPEND_SLASH) las GET
// sin slash -- una petición POST/PATCH/DELETE sin slash final se pierde en
// el 301, asi que el path base ya lo incluye.
const RESOURCE = "user-book-progress/";

/**
 * Obtiene el progreso de un libro específico para un usuario. Uso interno
 * de este modulo (calculateClassProgress) -- ya no existe un endpoint de
 * "un solo objeto + 404"; se filtra la lista por user+book y se toma el
 * unico elemento esperado, lanzando si no hay ninguno (mismo
 * comportamiento observable que el 404 viejo para quien la llama con
 * try/catch).
 * @param {number} userId - ID del usuario
 * @param {number} bookId - ID del libro
 * @returns {Promise} - Promesa con la respuesta de la API (response.data =
 *   el registro de progreso, igual que antes)
 */
export async function getBookProgress(userId, bookId) {
  try {
    const response = await axiosAuth().get(RESOURCE, {
      params: { user: userId, book: bookId },
    });
    const [progress] = response.data;
    if (!progress) {
      throw new Error("No se encontró progreso para este usuario y libro");
    }
    return { ...response, data: progress };
  } catch (error) {
    console.error("Error al obtener el progreso del libro:", error);
    throw error;
  }
}

/**
 * Marca un libro como completado con calificación 100.
 * @param {number} userId - ID del usuario
 * @param {number} bookId - ID del libro
 * @returns {Promise} - Promesa con la respuesta de la API (response.data =
 *   {book_progress, badges_assigned, has_new_badges, already_awarded},
 *   igual que antes)
 */
export async function markBookAsCompleted(userId, bookId) {
  try {
    const { data: progress } = await getOrCreateProgress(userId, bookId);
    const response = await axiosAuth().patch(`${RESOURCE}${progress.id}/`, {
      status: 1,
    });
    return response;
  } catch (error) {
    console.error("Error al marcar el libro como completado:", error);
    throw error;
  }
}

/**
 * Crea o actualiza el progreso de un libro para un usuario. Idempotente:
 * 200 si ya existia un registro para (userId, bookId), 201 si se creo uno
 * nuevo -- mismo contrato que antes.
 * @param {number} userId - ID del usuario
 * @param {number} bookId - ID del libro
 * @returns {Promise} - Promesa con la respuesta de la API
 */
export async function getOrCreateProgress(userId, bookId) {
  try {
    const response = await axiosAuth().post(RESOURCE, {
      user: userId,
      book: bookId,
    });
    return response;
  } catch (error) {
    console.error("Error al obtener o crear el progreso del libro:", error);
    throw error;
  }
}

/**
 * Actualiza el estado de progreso de un libro.
 * @param {number} id - ID del registro de progreso
 * @param {number} status - Nuevo estado (0: TODO, 1: Completado, 2: En progreso)
 * @returns {Promise} - Promesa con la respuesta de la API
 */
export async function updateBookStatus(id, status) {
  try {
    const response = await axiosAuth().patch(`${RESOURCE}${id}/`, {
      status: status,
    });
    return response;
  } catch (error) {
    console.error("Error al actualizar el estado del libro:", error);
    throw error;
  }
}

/**
 * Actualiza la calificación del libro.
 * @param {number} id - ID del registro de progreso
 * @param {number} calificacion - Nueva calificación (0-100)
 * @returns {Promise} - Promesa con la respuesta de la API
 */
export async function updateBookCalificacion(id, calificacion) {
  try {
    const response = await axiosAuth().patch(`${RESOURCE}${id}/`, {
      calificacion: calificacion,
    });
    return response;
  } catch (error) {
    console.error("Error al actualizar la calificación del libro:", error);
    throw error;
  }
}

/**
 * Elimina el registro de progreso de un libro.
 * @param {number} id - ID del registro de progreso
 * @returns {Promise} - Promesa con la respuesta de la API
 */
export async function deleteBookProgress(id) {
  try {
    const response = await axiosAuth().delete(`${RESOURCE}${id}/`);
    return response;
  } catch (error) {
    console.error("Error al eliminar el progreso del libro:", error);
    throw error;
  }
}

/**
 * Obtiene la cantidad total de libros completados por un usuario. El
 * endpoint viejo devolvia el conteo directamente; el nuevo devuelve la
 * lista filtrada (status=completed), asi que el conteo se calcula acá con
 * `.length` para preservar el contrato `.data.completed_books` que ya
 * consumen MyClasses.jsx y BookCompletedCounter.jsx.
 * @param {number} userId - ID del usuario
 * @returns {Promise} - Promesa con la respuesta de la API
 */
export async function getBooksCompleted(userId) {
  try {
    const response = await axiosAuth().get(RESOURCE, {
      params: { user: userId, status: "completed" },
    });
    return {
      ...response,
      data: { user_id: userId, completed_books: response.data.length },
    };
  } catch (error) {
    console.error("Error al obtener la cantidad de libros completados:", error);
    throw error;
  }
}

/**
 * Marca que los puntos fueron otorgados para un libro.
 * @param {number} userId - ID del usuario
 * @param {number} bookId - ID del libro
 * @returns {Promise} - Promesa con la respuesta de la API
 */
export async function markPointsAwarded(userId, bookId, points = 0) {
  try {
    const { data: progress } = await getBookProgress(userId, bookId);
    const response = await axiosAuth().patch(`${RESOURCE}${progress.id}/`, {
      points_awarded: true,
      points: points,
    });
    return response;
  } catch (error) {

    throw error;
  }
}

/**
 * Calcula el progreso de una clase basado en los libros completados.
 * @param {number} userId - ID del usuario
 * @param {Array} classBookIds - Array de IDs de libros que pertenecen a la clase
 * @returns {Promise<number>} - Porcentaje de progreso (0-100)
 */
export async function calculateClassProgress(userId, classBookIds) {
  try {
    if (!classBookIds || classBookIds.length === 0) {
      return 0;
    }

    let completedCount = 0;

    // Para cada libro de la clase, verificar si está completado
    for (const bookId of classBookIds) {
      try {
        const progressResponse = await getBookProgress(userId, bookId);
        // status = 1 significa completado
        if (progressResponse?.data?.status === 1) {
          completedCount++;
        }
      } catch (error) {
        // Si no existe progreso para este libro, no está completado

      }
    }

    // Calcular porcentaje: (completados / total) * 100
    const progressPercentage = Math.round((completedCount / classBookIds.length) * 100);


    return progressPercentage;
  } catch (error) {

    return 0;
  }
}
